import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/lang";
import { apps } from "@/lib/apps";
import { deleteText } from "@/lib/deleteAccount";
import { EASE } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const field = "w-full rounded-2xl border border-white/15 bg-white/[0.03] px-4 py-3.5 text-white placeholder:text-white/35 outline-none transition-[border-color,background-color] duration-300 focus:border-[#22d3ee] focus:bg-white/[0.06]";

export const DeletionForm = () => {
  const { lang } = useLang();
  const d = deleteText[lang];
  const [form, setForm] = useState({ email: "", app: "all", message: "", confirm: false });
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email.trim()) || !form.confirm) return toast.error(d.invalid);
    setState("sending");
    try {
      await axios.post(`${API}/deletion-requests`, { email: form.email.trim(), app: form.app, message: form.message.trim() || null, lang });
      setState("done");
    } catch {
      setState("idle");
      toast.error(d.error);
    }
  };

  const chips = [{ slug: "all", name: d.all }, ...apps.map((a) => ({ slug: a.slug, name: a.name }))];

  return (
    <div className="glass mt-6 rounded-[28px] p-6 sm:p-8" data-testid="deletion-form-card">
      <AnimatePresence mode="popLayout" initial={false}>
        {state === "done" ? (
          <motion.div key="done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: EASE }} data-testid="deletion-success">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#22d3ee] to-[#5cf2a0] text-[#05070f]"><Check className="h-6 w-6" /></span>
            <p className="mt-6 font-display text-2xl font-bold">{d.successTitle}</p>
            <p className="mt-3 max-w-lg text-white/65">{d.successSub}</p>
            <button type="button" data-testid="deletion-another-button" onClick={() => { setForm({ email: "", app: "all", message: "", confirm: false }); setState("idle"); }} className="link-underline mt-6 text-sm text-white/70">{d.another}</button>
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} noValidate initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6" data-testid="deletion-form">
            <label className="block">
              <span className="text-sm text-white/60">{d.email}</span>
              <input type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${field} mt-2`} placeholder="you@email.com" data-testid="deletion-email-input" />
            </label>
            <div>
              <span className="text-sm text-white/60">{d.app}</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {chips.map((c) => (
                  <button key={c.slug} type="button" data-testid={`deletion-app-chip-${c.slug}`} onClick={() => setForm({ ...form, app: c.slug })} className={`rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color] duration-300 ${form.app === c.slug ? "border-transparent bg-white text-[#05070f]" : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-sm text-white/60">{d.message}</span>
              <textarea rows={3} maxLength={1000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${field} mt-2 resize-none`} data-testid="deletion-message-input" />
            </label>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-white/70">
              <input type="checkbox" checked={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.checked })} className="mt-0.5 h-5 w-5 shrink-0 accent-[#22d3ee]" data-testid="deletion-confirm-checkbox" />
              {d.confirm}
            </label>
            <button type="submit" disabled={state === "sending"} className="btn-primary disabled:opacity-60" data-testid="deletion-submit-button">
              {state === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" />{d.sending}</> : d.submit}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
